import type { StorageReference, UploadResult } from "firebase/storage";

export const UPLOAD_ATTEMPTS = 3;
export const UPLOAD_TIMEOUT_MS = 90_000;

export type Uploader = (
  ref: StorageReference,
  blob: Blob,
) => Promise<UploadResult>;

export const isRetryable = (err: unknown): boolean => {
  const code = (err as { code?: string })?.code;
  const message = (err as { message?: string })?.message ?? "";
  if (code === "storage/canceled") return false;
  if (code && code.startsWith("storage/")) return true;
  if (message.includes("ERR_UPLOAD_FILE_CHANGED")) return true;
  if (message.includes("Failed to fetch")) return true;
  if (message.includes("NetworkError")) return true;
  if (message === "UPLOAD_TIMEOUT") return true;
  return false;
};

export const withTimeout = <T>(p: Promise<T>, ms: number): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("UPLOAD_TIMEOUT")), ms);
    p.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export const uploadBlobWithRetry = async (
  storageRef: StorageReference,
  blob: Blob,
  uploader: Uploader,
): Promise<UploadResult> => {
  let lastErr: unknown;
  for (let attempt = 0; attempt < UPLOAD_ATTEMPTS; attempt++) {
    try {
      return await withTimeout(uploader(storageRef, blob), UPLOAD_TIMEOUT_MS);
    } catch (err) {
      lastErr = err;
      const isLast = attempt === UPLOAD_ATTEMPTS - 1;
      if (isLast || !isRetryable(err)) throw err;
      await sleep(500 * 2 ** attempt);
    }
  }
  throw lastErr;
};
